import React, { FunctionComponent } from 'react'
import Grid from '@sport1/react-elements/Grid'
import Touchable from '@sport1/react-elements/Touchable'
import FontelloIcon from '@sport1/react-elements/FontelloIcon'
import Divider from '../../../../BaseComponents/Divider'
import NonFlexingContainer from '../../../../Layouts/NonFlexingContainer'
import BasicText from '../../../../Theme/Fonts/BasicText'
import SmallBodyFont from '../../../../Theme/Fonts/SmallBody'
import CaptionFont from '../../../../Theme/Fonts/Caption'
import FlagImage from '../../../../BaseComponents/Image/Flag'
import { TestID } from '../../../../utils/PropTypesDefinitions'
import PlayerImage from '../../../../BaseComponents/Image/Player'

export type TopPerformerItemProps = TestID & {
    /**
     * rank of the player
     */
    rank?: number
    /**
     * firstname of the player
     */
    firstname: string
    /**
     * lastname of the player
     */
    lastname: string
    /**
     * text that will be displayed beneath the names
     */
    stats?: string
    /**
     * team that will displayed between stats and player name
     */
    team?: string
    /**
     * score of the player
     */
    value: string | number
    /**
     * url of the player image
     */
    imageUrl?: string
    /*
     * indicate the image type
     */
    imageType?: 'DEFAULT' | 'FLAG'
    /**
     * info about imageUrl for seo
     */
    alt?: string
    /**
     * flag to add a divider at the bottom of the component
     */
    divider?: boolean
    /**
     * flag to indicate if the player is favored
     * */
    isFavorite?: boolean
    /**
     * action called when pressed
     * @TJS-type: function
     */
    onPress?: () => void
    /**
     * action called when pressed on the favorite-icon
     * @TJS-type: function
     */
    onPressFavorite?: () => void
}

const TopPerformerItem: FunctionComponent<TopPerformerItemProps> = ({
    rank,
    firstname,
    lastname,
    imageUrl,
    imageType = 'DEFAULT',
    alt,
    stats,
    team,
    value,
    divider,
    isFavorite,
    onPress,
    onPressFavorite,
    testID,
}) => {
    return (
        <Touchable testID={testID} onPress={onPress}>
            <Grid horizontal paddingX={5} noExpand backgroundColor="pearl">
                {rank ? (
                    <NonFlexingContainer width={33} centerVertical height={60} marginRight={3}>
                        <BasicText
                            fontSize="FS16"
                            lineHeight="LH16"
                            bold
                            color="onyx-4"
                            numberOfLines={1}
                        >
                            {`${rank}.`}
                        </BasicText>
                    </NonFlexingContainer>
                ) : null}
                <NonFlexingContainer centerVertical centerHorizontal width={36} height={60}>
                    {imageType === 'FLAG' ? (
                        <FlagImage imageUrl={imageUrl} alt={alt} width={28} size="S" />
                    ) : (
                        <PlayerImage imageUrl={imageUrl} alt={alt} width={36} size="S" />
                    )}
                </NonFlexingContainer>
                <Grid height={team && stats ? 80 : 60} paddingX={3} centerVertical minWidth={0}>
                    <SmallBodyFont numberOfLines={1}>{`${lastname}, ${firstname}`}</SmallBodyFont>
                    {team ? (
                        <CaptionFont color="keshi-3" numberOfLines={1}>
                            {team}
                        </CaptionFont>
                    ) : null}
                    {stats ? (
                        <CaptionFont color="keshi-3" numberOfLines={1}>
                            {stats}
                        </CaptionFont>
                    ) : null}
                </Grid>
                <NonFlexingContainer height={60} centerVertical>
                    <BasicText
                        fontSize="FS16"
                        lineHeight="LH24"
                        color="onyx"
                        bold
                    >{`${value}`}</BasicText>
                </NonFlexingContainer>
                {onPressFavorite ? (
                    <Touchable onPress={onPressFavorite}>
                        <Grid paddingBottom={1} noExpand paddingLeft={4} height={60} centerVertical>
                            <FontelloIcon
                                fontSize={18}
                                name={isFavorite ? 'favorite-active' : 'favorite'}
                                color={isFavorite ? 'ORANGE' : 'keshi-3'}
                            />
                        </Grid>
                    </Touchable>
                ) : null}
            </Grid>
            {divider ? (
                <Grid paddingX={5} backgroundColor="pearl">
                    <Divider dark />
                </Grid>
            ) : null}
        </Touchable>
    )
}
export default TopPerformerItem
